import { IntlShape } from 'react-intl';

import { resolveTier, tierCoversQuantity } from 'store/resource/price-tiers';
import { AddOn } from 'store/resource/types';

/* The units catalog-admin offers by name; anything else is an admin's own string, rendered as typed. */
const KNOWN_UNITS = ['each', 'hour', 'sq_ft', 'day'];

const unitLabel = (intl: IntlShape, unitType: string): string =>
  KNOWN_UNITS.includes(unitType) ? intl.formatMessage({ id: `add_on_unit_${unitType}` }) : unitType;

interface SubtitleContext {
  intl: IntlShape;
  /* Already formatted by the caller, which owns the currency and locale for its own surface. */
  price: string;
  quantity: number;
  minQuantity: number;
}

/*
 * The price line under an add-on, or null when none can be stated.
 *
 * Reads `pricing_type`, never the number: catalog resolves no price for a quote-required product, so
 * it arrives as `price: 0` exactly like a free one.
 */
export const addOnSubtitle = (addOn: AddOn, { intl, price, quantity, minQuantity }: SubtitleContext): string | null => {
  /* The 0 on the wire is not a price. Suppress on the reason, never on the number. */
  if (addOn.unavailable_reason === 'price_unavailable') {
    return null;
  }

  if (addOn.pricing_type === 'quote_required') {
    return intl.formatMessage({ id: 'add_on_price_on_request' });
  }

  if (addOn.pricing_type === 'free') {
    return intl.formatMessage({ id: 'add_on_price_free' });
  }

  /*
   * `flat` says the price is not per anything, so neither a suffix nor a band range can qualify it.
   * Ahead of the band lookup: behind it, a tiered flat product reads "Tiered: $250 / flat (1-5)".
   */
  if (addOn.unit_type === 'flat') {
    return price;
  }

  const effectiveQuantity = Math.max(quantity, minQuantity, 1);
  const band = addOn.pricing_type === 'tiered' ? resolveTier(addOn.price_tiers, effectiveQuantity) : null;
  /*
   * `resolveTier` falls back to the FIRST band for a quantity no band covers, matching what work-orders
   * charge. The price is therefore right, but the range is not this quantity's -- printing it would say
   * "(5-10)" beside a selected 2. Keep the price, drop the range.
   */
  if (band && tierCoversQuantity(band, effectiveQuantity) && addOn.unit_type) {
    const unit = unitLabel(intl, addOn.unit_type);

    return band.max_quantity == null
      ? intl.formatMessage({ id: 'add_on_price_tiered_open' }, { price, unit, min: band.min_quantity })
      : intl.formatMessage(
          { id: 'add_on_price_tiered' },
          { price, unit, min: band.min_quantity, max: band.max_quantity },
        );
  }

  /*
   * `{price} each` is already idiomatic in all 19 catalogues, so the default unit reuses it rather
   * than composing "{price} / unit". Also the pre-helix fallback, while `unit_type` is absent.
   */
  if (!addOn.unit_type || addOn.unit_type === 'each') {
    return intl.formatMessage({ id: 'add_on_price_each' }, { price });
  }

  return intl.formatMessage({ id: 'add_on_price_per_unit' }, { price, unit: unitLabel(intl, addOn.unit_type) });
};
